/**
 * Responsive Layout Utility
 * 
 * Provides helpers for adapting UI layouts to different screen sizes
 * in RESIZE mode (no letterboxing).
 */

export type ScreenBreakpoint = 'portrait' | 'landscape' | 'desktop'

export interface ResponsiveConfig {
    width: number
    height: number
    breakpoint: ScreenBreakpoint
    isPortrait: boolean
    isLandscape: boolean
    isDesktop: boolean
    aspectRatio: number
    isMobile: boolean
}

export class ResponsiveLayout {
    // Button sizing constants
    private static readonly IOS_BUTTON_SCALE_FACTOR = 0.70 // Buttons are 75% of original size (25% reduction)
    private static readonly NARROW_SCREEN_WIDTH_FACTOR = 0.7
    private static readonly NARROW_SCREEN_HEIGHT_FACTOR = 0.9

    /**
     * Detect if the current device is iOS
     */
    private static isIOSDevice(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent))
    }

    /**
     * Get current screen configuration
     */
    static getConfig(scene: Phaser.Scene): ResponsiveConfig {
        const width = scene.scale.width
        const height = scene.scale.height
        const aspectRatio = width / height

        // Detect mobile vs desktop (rough heuristic)
        const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0

        // Determine breakpoint
        let breakpoint: ScreenBreakpoint
        if (aspectRatio < 1) {
            breakpoint = 'portrait'
        } else if (aspectRatio < 1.5 || (isMobile && aspectRatio < 2)) {
            breakpoint = 'landscape'
        } else {
            breakpoint = 'desktop'
        }

        return {
            width,
            height,
            breakpoint,
            isPortrait: breakpoint === 'portrait',
            isLandscape: breakpoint === 'landscape',
            isDesktop: breakpoint === 'desktop',
            aspectRatio,
            isMobile
        }
    }

    /**
     * Scale a value based on screen size with min/max constraints
     */
    static scaleValue(base: number, screenSize: number, referenceSize: number = 1920, min?: number, max?: number): number {
        const scaled = base * (screenSize / referenceSize)
        if (min !== undefined && scaled < min) return min
        if (max !== undefined && scaled > max) return max
        return scaled
    }

    /**
     * Get centered X position
     */
    static centerX(scene: Phaser.Scene): number {
        return scene.scale.width / 2
    }

    /**
     * Get centered Y position
     */
    static centerY(scene: Phaser.Scene): number {
        return scene.scale.height / 2
    }

    /**
     * Calculate responsive dialog dimensions
     * Returns dimensions that fit within the screen with safe margins
     */
    static getDialogSize(
        scene: Phaser.Scene,
        baseWidth: number,
        baseHeight: number,
        options: {
            maxWidthPercent?: number,  // Max % of screen width (default 90%)
            maxHeightPercent?: number, // Max % of screen height (default 85%)
            minWidth?: number,
            minHeight?: number,
            maintainAspectRatio?: boolean
        } = {}
    ): { width: number, height: number } {
        const config = this.getConfig(scene)

        const maxWidthPercent = options.maxWidthPercent ?? 0.9
        const maxHeightPercent = options.maxHeightPercent ?? 0.85
        const minWidth = options.minWidth ?? 280
        const minHeight = options.minHeight ?? 200

        let width = baseWidth
        let height = baseHeight

        const maxWidth = config.width * maxWidthPercent
        const maxHeight = config.height * maxHeightPercent

        // Scale down if too large
        if (width > maxWidth) {
            if (options.maintainAspectRatio) {
                const scale = maxWidth / width
                width = maxWidth
                height = height * scale
            } else {
                width = maxWidth
            }
        }

        if (height > maxHeight) {
            if (options.maintainAspectRatio) {
                const scale = maxHeight / height
                height = maxHeight
                width = width * scale
            } else {
                height = maxHeight
            }
        }

        // Enforce minimums
        width = Math.max(width, minWidth)
        height = Math.max(height, minHeight)

        return { width, height }
    }

    /**
     * Get device safe area insets (for notch, dynamic island, home indicator)
     * Returns pixel values
     */
    static getSafeArea(): { top: number, right: number, bottom: number, left: number } {
        // For iOS/Safari with notch support, we need to check actual viewport-fit coverage
        // Create a temporary element to measure safe area insets
        const testDiv = document.createElement('div')
        testDiv.style.position = 'fixed'
        testDiv.style.top = '0'
        testDiv.style.left = '0'
        testDiv.style.width = '100vw'
        testDiv.style.height = '100vh'
        testDiv.style.pointerEvents = 'none'
        testDiv.style.visibility = 'hidden'
        document.body.appendChild(testDiv)

        // Try to read env() variables directly
        const computed = getComputedStyle(testDiv)

        // Parse safe area insets from CSS env() - these are set by the browser on iOS
        const parseInset = (value: string): number => {
            if (!value || value === '0px') return 0
            const num = parseFloat(value)
            return isNaN(num) ? 0 : num
        }

        // iOS provides these through env() CSS variables
        const topInset = parseInset(computed.getPropertyValue('padding-top')) ||
            parseInset(getComputedStyle(document.documentElement).getPropertyValue('--sat')) || 0
        const rightInset = parseInset(computed.getPropertyValue('padding-right')) ||
            parseInset(getComputedStyle(document.documentElement).getPropertyValue('--sar')) || 0
        const bottomInset = parseInset(computed.getPropertyValue('padding-bottom')) ||
            parseInset(getComputedStyle(document.documentElement).getPropertyValue('--sab')) || 0
        const leftInset = parseInset(computed.getPropertyValue('padding-left')) ||
            parseInset(getComputedStyle(document.documentElement).getPropertyValue('--sal')) || 0

        document.body.removeChild(testDiv)

        // On iOS devices with notch in landscape, we typically have:
        // - Left inset: ~44-47px (notch side)
        // - Right inset: ~44-47px (opposite side)  
        // - Top: 0-20px
        // - Bottom: 0-21px (home indicator)

        // If we detect we're likely on iOS (based on user agent or viewport-fit) 
        // but insets are 0, provide sensible defaults
        const isIOS = this.isIOSDevice()

        if (isIOS && topInset === 0 && leftInset === 0) {
            // Provide default safe zones for iOS in landscape
            const width = window.innerWidth
            const height = window.innerHeight
            const isLandscape = width > height

            if (isLandscape) {
                // In landscape, the notch/dynamic island is on the side
                return {
                    top: 0,
                    right: 44,  // Safe area for notch side
                    bottom: 21, // Home indicator
                    left: 44    // Safe area for opposite side
                }
            } else {
                // In portrait, notch is at top
                return {
                    top: 47,    // Status bar + notch
                    right: 0,
                    bottom: 34, // Home indicator
                    left: 0
                }
            }
        }

        return {
            top: topInset,
            right: rightInset,
            bottom: bottomInset,
            left: leftInset
        }
    }

    /**
     * Calculate button size based on screen
     */
    static getButtonSize(scene: Phaser.Scene, baseWidth: number = 200, baseHeight: number = 56): { width: number, height: number } {
        const config = this.getConfig(scene)
        const isIOS = this.isIOSDevice()

        // Scale buttons down on small screens
        let width = baseWidth
        let height = baseHeight

        // Shrink buttons on iOS devices
        if (isIOS) {
            width = baseWidth * this.IOS_BUTTON_SCALE_FACTOR
            height = baseHeight * this.IOS_BUTTON_SCALE_FACTOR
        }

        if (config.width < 500) {
            // Very narrow screen
            width = Math.min(width, config.width * this.NARROW_SCREEN_WIDTH_FACTOR)
            height = height * this.NARROW_SCREEN_HEIGHT_FACTOR
        } else if (config.isPortrait && !isIOS) {
            // Portrait mode - make buttons slightly wider (but not on iOS where they're already smaller)
            width = Math.min(width * 1.2, config.width * 0.6)
        }

        return { width, height }
    }

    /**
     * Calculate responsive font size
     */
    static getFontSize(scene: Phaser.Scene, baseSize: number): number {
        const config = this.getConfig(scene)
        const referenceWidth = config.isPortrait ? 480 : 960

        const scaled = baseSize * (config.width / referenceWidth)

        // Clamp to reasonable limits
        return Math.max(12, Math.min(scaled, baseSize * 1.5))
    }

    /**
     * Calculate grid of items that fit on screen
     */
    static calculateGrid(
        scene: Phaser.Scene,
        itemWidth: number,
        itemHeight: number,
        itemCount: number,
        spacing: number = 20
    ): { columns: number, rows: number, startX: number, startY: number, itemWidth: number, itemHeight: number } {
        const config = this.getConfig(scene)

        // Determine columns based on screen width
        let columns: number
        if (config.isPortrait) {
            columns = 1
        } else if (config.width < 800) {
            columns = 2
        } else {
            columns = Math.min(3, itemCount)
        }

        const rows = Math.ceil(itemCount / columns)

        // Scale items if needed
        const availableWidth = config.width - (spacing * (columns + 1))
        const maxItemWidth = availableWidth / columns

        if (itemWidth > maxItemWidth) {
            const scale = maxItemWidth / itemWidth
            itemWidth = maxItemWidth
            itemHeight = itemHeight * scale
        }

        // Calculate grid start position
        const totalWidth = (itemWidth * columns) + (spacing * (columns - 1))
        const totalHeight = (itemHeight * rows) + (spacing * (rows - 1))

        const startX = (config.width - totalWidth) / 2
        const startY = (config.height - totalHeight) / 2

        return { columns, rows, startX, startY, itemWidth, itemHeight }
    }

    /**
     * Get responsive margin/padding values
     */
    static getMargins(scene: Phaser.Scene): { horizontal: number, vertical: number, small: number, medium: number, large: number } {
        const config = this.getConfig(scene)

        const base = config.isPortrait ? 20 : 40

        return {
            horizontal: this.scaleValue(base, config.width, 960, 10, 60),
            vertical: this.scaleValue(base, config.height, 640, 10, 60),
            small: this.scaleValue(10, Math.min(config.width, config.height), 640, 5, 20),
            medium: this.scaleValue(20, Math.min(config.width, config.height), 640, 10, 40),
            large: this.scaleValue(40, Math.min(config.width, config.height), 640, 20, 80)
        }
    }
}
